import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { getTeams } from '../../services/team';

const EmployeeForm = ({ employee, onClose, onSubmit, isSubmitting }) => {
  const [teams, setTeams] = useState([]);
  const [teamLoadError, setTeamLoadError] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: employee?.name || '',
      email: employee?.email || '',
      team_id: employee?.team_id || '',
      is_active: employee?.is_active ?? true
    }
  });

  useEffect(() => {
    reset({
      name: employee?.name || '',
      email: employee?.email || '',
      team_id: employee?.team_id || '',
      is_active: employee?.is_active ?? true
    });
  }, [employee, reset]);

  useEffect(() => {
    getTeams()
      .then(setTeams)
      .catch((error) => {
        console.error('Failed to load teams:', error);
        setTeamLoadError('Unable to load teams. Please close this form and try again.');
      });
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative mx-auto mt-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {employee ? 'Edit Employee' : 'Add Employee'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xl font-semibold text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {teamLoadError && (
            <p role="alert" className="text-sm text-red-600">{teamLoadError}</p>
          )}

          <div>
            <label htmlFor="employee-name" className="mb-1 block text-sm font-medium text-gray-700">Name</label>
            <input
              id="employee-name"
              {...register('name', { required: 'Name is required' })}
              className="block w-full rounded-md border border-gray-300 px-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Employee name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="employee-email" className="mb-1 block text-sm font-medium text-gray-700">Email</label>
            <input
              id="employee-email"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Enter a valid email address'
                }
              })}
              className="block w-full rounded-md border border-gray-300 px-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="employee@company.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
          </div>

          <div>
            <label htmlFor="employee-team" className="mb-1 block text-sm font-medium text-gray-700">Team</label>
            <select
              id="employee-team"
              {...register('team_id', { required: 'Please select a team' })}
              className="block w-full rounded-md border border-gray-300 px-4 py-3 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="">Select a team</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            {errors.team_id && <p className="mt-1 text-sm text-red-600">{errors.team_id.message}</p>}
          </div>

          {employee && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" {...register('is_active')} />
              Active account
            </label>
          )}

          {!employee && (
            <p className="text-sm text-gray-500">
              A temporary password will be generated and sent to this employee by email.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : employee ? 'Save Changes' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;
